import { useRecoilValue } from 'recoil'
import usersTableFields from './usersTable.fields.tsx'
import { useCallback, useMemo } from 'react'
import { IOrganization } from '@/pages/Organizations/organizations.types.ts'
import useTableWithRequest from '@/shared/Tables/Table/ctrl/useTableWithRequest.ts'
import useItemsSetter from '@/recoil/useItemsSetter'
import useSidePageWithTableCtrl from '@/shared/SidePage/useSidePageWithTableCtrl.ts'
import Table from '@/shared/Tables/Table'
import usersApi from '@/pages/Users/users.api.ts'
import usersAtom from '@/pages/Users/users.atom.ts'
import { IUser } from '@/pages/Users/users.types.ts'
import UserSidePage from '@/pages/Users/components/UserSidePage'
import usersUtils from '../../users.utils.ts'
import userUtils from '@/pages/User/user.utils.ts'

interface IProps {
  positionFilter?: string[]
  activeFilter?: number
  searchQuery?: string
  organizationId: IOrganization['id']
}

const OrganizationUsersTable = (props: IProps) => {
  const items = useRecoilValue(usersAtom).items
  const sidePageCtrl = useSidePageWithTableCtrl<IUser>(items)

  const dataLoad = useCallback((params: any) => {
    return usersApi.list({ ...params, organizationId: props.organizationId })
  }, [])

  const setItems = useItemsSetter(usersAtom)

  const tableCtrl = useTableWithRequest({
    serverSideOrder: false,
    fields: usersTableFields,
    tableKey: `organization/users`,
    itemKey: 'id',
    dataFetcher: dataLoad,
    items,
    setItems,
    storageType: 'inMemory',
  })

  const filteredResults = useMemo(() => {
    return tableCtrl.orderedItems.filter((u) => {
      const byPosition =
        !props.positionFilter?.length || props.positionFilter.includes(usersUtils.normalizePosition(u.position))
      const byActive = props.activeFilter == null || u.active === Boolean(props.activeFilter)

      const searchString = [userUtils.getFullName(u.userInfo), u.position, u.userInfo.phoneNumber, u.email]
        .join('$')
        .toLowerCase()
      const bySearchQuery = !props.searchQuery || searchString.includes(props.searchQuery.trim().toLowerCase())

      return byPosition && byActive && bySearchQuery
    })
  }, [tableCtrl.orderedItems, props.positionFilter, props.activeFilter, props.searchQuery])

  return (
    <>
      <Table {...tableCtrl} items={filteredResults as any} itemKey="id" onClick={sidePageCtrl.handleItemPageOpen} />
      <UserSidePage
        onCloseAnimationEnd={sidePageCtrl.handleCloseAnimationEnd}
        user={sidePageCtrl.selectedItem}
        open={sidePageCtrl.sidePageOpen}
        onClose={sidePageCtrl.handleClosePage}
      />
    </>
  )
}

export default OrganizationUsersTable
